import { Button } from "@constructorfleet/ui";
import { createPortal } from "react-dom";
import { useAuth } from "../context/auth-context";
import type { AbVariant } from "../lib/admin-experiments-api";
import type { UseAbExperimentResult } from "../hooks/use-ab-experiment";

type Props = Pick<UseAbExperimentResult, "variant" | "assignment" | "setOverride">;

/**
 * Floating variant toggle shown only to admins when an A/B experiment is
 * active on the current page.  Regular users see their assigned variant
 * with no UI chrome.
 */
export function AbExperimentOverlay({ variant, assignment, setOverride }: Props) {
  const { auth } = useAuth();

  if (!assignment || auth?.role !== "admin") return null;

  const { variantALabel, variantBLabel } = assignment.experiment;

  const toggle = (
    <div
      className="ab-experiment-overlay-shell"
      style={{
        position: "fixed",
        top: "5.25rem",
        right: "clamp(1rem, 3vw, 3rem)",
        zIndex: 20,
      }}
    >
      <div
        className="ab-experiment-overlay"
        role="group"
        aria-label={`A/B experiment: ${assignment.experiment.name}`}
        style={{
          display: "inline-flex",
          gap: "var(--cf-space-sm)",
          alignItems: "center",
          padding: "0.35rem",
          borderRadius: "999px",
          background: "var(--cf-surface)",
          border: "1px solid var(--cf-border-color, rgba(0, 0, 0, 0.12))",
          boxShadow: "var(--cf-shadow-lg)",
          backdropFilter: "blur(8px)",
        }}
      >
        <VariantButton
          label={variantALabel}
          active={variant === "a"}
          onClick={() => setOverride("a" as AbVariant)}
        />
        <VariantButton
          label={variantBLabel}
          active={variant === "b"}
          onClick={() => setOverride("b" as AbVariant)}
        />
      </div>
    </div>
  );

  return createPortal(toggle, document.body);
}

function VariantButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? "solid" : "outline"}
      tone={active ? "brand" : "neutral"}
      aria-pressed={active}
      onClick={onClick}
    >
      {label}
    </Button>
  );
}
